// src/Login.jsx
import React, { useState, useEffect } from "react";
import { auth } from "../firebase/config";
import {
  mapGoogleWorkspaceAuthError,
  signInWithPortalGoogle,
  signInWithGoogleWorkspace,
  getPageOriginForDebug
} from "../firebase/googleAuth";
import { getClientPortalKind } from "../firebase/portalHosts";
import { Shield, Loader2 } from 'lucide-react';
import "./Login.css";
import { useMesPatrio } from '../hooks/useMesPatrio';
import { MesPatrioLoginDress } from '../components/mesPatrio/MesPatrioDress';
import VivaMexicoCeremony from '../components/mesPatrio/VivaMexicoCeremony';

const SPACE_LOGIN_COPY = {
  title: 'Tu espacio Plexarian',
  subtitle: 'Entra con tu cuenta de Google Workspace. Soporte para todo el equipo; licencias y pagos solo si administras la consola.'
};

const PORTAL_LOGIN_COPY = {
  licencias: {
    title: 'Solicitud de licencias',
    subtitle: 'Entra con tu cuenta de Google Workspace. Solo pueden entrar los administradores y las personas a las que ellos les dieron permiso de solicitar licencias.'
  },
  espacio: SPACE_LOGIN_COPY,
  capacitaciones: {
    title: 'Mis Capacitaciones',
    subtitle: 'Grabaciones y materiales de tus sesiones SkillSync'
  }
};

/** Google trata localhost y 127.0.0.1 como orígenes distintos. */
function normalizeLocalOrigin() {
  if (typeof window === 'undefined') return;
  const { hostname, protocol, port, pathname, search, hash } = window.location;
  if (hostname !== '127.0.0.1') return;
  const p = port ? `:${port}` : '';
  window.location.replace(`${protocol}//localhost${p}${pathname}${search}${hash}`);
}

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const portalKind = getClientPortalKind();
  const portalCopy = portalKind ? PORTAL_LOGIN_COPY[portalKind] : null;
  const { enabled: mesPatrio, grito } = useMesPatrio();
  const showPatrio = mesPatrio && !portalKind;

  useEffect(() => {
    normalizeLocalOrigin();
  }, []);

  useEffect(() => {
    if (portalKind === 'capacitaciones') document.title = 'Mis Capacitaciones | Plexarian';
    else if (portalKind === 'licencias') document.title = 'Solicitud de licencias | Plexarian';
    else if (portalKind === 'espacio') document.title = 'Tu espacio | Plexarian';
    else document.title = 'NEXUS | Plexarian';
    const onVisible = () => {
      if (document.visibilityState === 'visible') setLoading(false);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [portalKind]);

  const handlePortalGoogleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithPortalGoogle();
      setLoading(false);
    } catch (err) {
      console.error(err);
      if (auth.currentUser) {
        setLoading(false);
        return;
      }
      setError(mapGoogleWorkspaceAuthError(err?.code, err?.message));
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await signInWithGoogleWorkspace();
      if (String(result?.method || '').includes('redirect')) return;
    } catch (err) {
      console.error(err);
      if (!auth.currentUser) {
        setError(mapGoogleWorkspaceAuthError(err?.code, err?.message));
      }
    } finally {
      if (!auth.currentUser) setLoading(false);
    }
  };

  const debugOrigin = typeof window !== 'undefined' ? getPageOriginForDebug() : '';

  return (
    <div
      className={[
        'nexus-login-container',
        portalKind ? 'client-portal-bg portal-login' : '',
        portalKind === 'licencias' ? 'portal-login--licencias' : '',
        portalKind === 'capacitaciones' ? 'portal-login--capacitaciones' : '',
        showPatrio ? 'patrio-on' : ''
      ].filter(Boolean).join(' ')}
    >
      {showPatrio && <MesPatrioLoginDress grito={grito} />}
      {showPatrio && <VivaMexicoCeremony />}
      {portalKind && (
        <div className="portal-login-ambient" aria-hidden="true">
          <span className="portal-login-orb portal-login-orb--pink" />
          <span className="portal-login-orb portal-login-orb--purple" />
          <span className="portal-login-grid" />
        </div>
      )}

      <div className={`nexus-glass-card${portalKind ? ' portal-login-card' : ''}`}>
        {portalKind && <div className="portal-login-accent" aria-hidden="true" />}

        <div className="logo-section">
          <img
            src="/assets/plexarian.svg"
            alt="Plexarian"
            className="nexus-logo-img"
          />
        </div>

        {portalCopy ? (
          <div className="portal-login-copy">
            <h1 className="portal-login-title">{portalCopy.title}</h1>
            <p className="portal-login-subtitle">{portalCopy.subtitle}</p>
          </div>
        ) : (
          <h1 className="nexus-title">NEXUS</h1>
        )}

        {portalKind ? (
          <div className="portal-login-actions">
            <button
              type="button"
              className="portal-google-btn"
              onClick={handlePortalGoogleLogin}
              disabled={loading}
              title="Acceder con Google Workspace"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt=""
                className="google-icon-small"
              />
              <span>Acceder con Google</span>
            </button>
            {loading && (
              <div className="portal-login-loading">
                <Loader2 className="spin" size={16} /> Entrando…
              </div>
            )}
            {error && (
              <div className="portal-login-error" role="alert">
                {error}
              </div>
            )}
            {process.env.NODE_ENV === 'development' && debugOrigin && (
              <div className="portal-login-origin-hint" title="Origen de la página">
                Origen: {debugOrigin}
              </div>
            )}
          </div>
        ) : (
          <>
            <p className="nexus-login-hint">Entra con tu cuenta de Google Workspace.</p>
            <button
              type="button"
              className="btn-google-nexus"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="spin" size={16} />
              ) : (
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt=""
                  className="google-icon-small"
                />
              )}
              <span>{loading ? 'Entrando…' : 'Google Workspace'}</span>
            </button>
            {error && (
              <div className="nexus-login-error" role="alert">
                {error}
              </div>
            )}
          </>
        )}

        <div className={`nexus-footer${portalKind ? ' portal-login-footer' : ''}`}>
          <Shield size={12} aria-hidden="true" />
          <span>Conexión segura TLS</span>
        </div>
      </div>
    </div>
  );
};

export default Login;
